import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from "@angular/core";
import { NewsDetailService } from "./newsDetail.service";
import { NewsDetailViewModel } from "../../shared/models/news.model";
import { CoreLogService } from "../../services/log.service";
import { ActivatedRoute } from "@angular/router";
import { Subscription } from "rxjs";
import { InternationalisationService } from "../../forked/angular-internationalisation";
import { Paging } from "../../shared/paging";
import Utils from "../../shared/utils";
import { Location } from "@angular/common";
import { ScrollHelper } from "../../shared/scrollHelper";
import { RatingService } from "../../services/rating.service";
import { LikeService } from "../../services/like.service";
import { ContentType, SortDirection, SnackBarMessageType } from "../../shared/contentTypes";
import { SortOption } from "../../shared/models/sort.model";
import { PagingHelper } from "../../shared/pagingHelper";
import { SnackBarService, snackBarMessageKey } from "../../services/snackBar.service";
import { LocationViewModel, NavigationTitleViewModel } from "../../shared/models";
import { RouterHelperService } from "../../services/routerHelper.service";
import { OverlaySpinnerService } from "../overlayspinner/overlaySpiner.service";
import { BottomSheetHelperService } from "../../services/bottomSheetHelper.service";
import { RatingDialogComponent } from "../../modalDialogs/ratingdialog/ratingDialog.component";
import { FilterLocationDialogComponent } from "../../modalDialogs/filterLocationDialog/filterLocationDialog.component";

@Component({
  selector: "newsdetail",
  templateUrl: "./newsDetail.component.html",
  styleUrls: ["./newsDetail.component.scss"],
  providers: [NewsDetailService, OverlaySpinnerService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewsDetailComponent implements OnInit, OnDestroy {

  // set animation movement
  public floatingTextActivated: boolean = false; // to activate animation if header in wider then the width of page.
  public animationObject = { value: "left", params: { shiftPixels: "0px", initialShift: "0px", initialtime: "0s", startPosition: "0px" } };
  private shiftPixels = 0; // used for shift pixel in floating animation

  public hasData: boolean = false; // check if there is data for this page;
  private newsId: number | string;
  public pageTitle: NavigationTitleViewModel = new NavigationTitleViewModel(); // Used as a title at the header of the page
  // if there is a targetScrollId page must be scrolled to top to show this item at the top of the page
  private targetScrollId: number = 0;
  public newsViewModel: NewsDetailViewModel = new NewsDetailViewModel;
  //public paging: Paging<NewsDetailViewModel>;
  //private pagingHelper: PagingHelper;
  private sortOption: SortOption[];
  public locationFilterIds: number[];
  //private filterFunction: ((item: NewsDetailViewModel) => boolean)[];

  public filterLocations: LocationViewModel[];
  private _langSubscriber: Subscription;
  private _newsChangesSubscriber: Subscription;
  private ratingChangesSubscriber: Subscription;
  public contentType = ContentType;

  constructor(private _newsService: NewsDetailService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _coreLogService: CoreLogService,
    private _ratingService: RatingService,
    private _likeService: LikeService,
    private _activatedRoute: ActivatedRoute,
    private _internationalisationService: InternationalisationService,
    private _routerHelperService: RouterHelperService,
    private _snackBarService: SnackBarService,
    private _overlaySpinnerService: OverlaySpinnerService,
    private _scrollHelper: ScrollHelper,
    private _bottomSheetHelperService: BottomSheetHelperService) {

  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("NewsDetailComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  public ngOnInit(): void {
    const me = this;

    me.getRouteData();

    me._langSubscriber = me._internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });

    // reload event data if data has been changed in storage.
    me._newsChangesSubscriber = me._newsService.ObserveChanges.subscribe((changed) => {
      me.ReloadDataAndRender();
    });

    me.setRatingSubscriber();
    me.ReloadDataAndRender();
  }

  /**
 * Set the title of the page by title Data in route data
 * This data has been set in RouterHelperService by each navigation End Event
 */
  private getRouteData() {
    const me = this;
    me._activatedRoute.paramMap.subscribe(params => {
      me.newsId = params.get("id");
    });
  }

  private setRatingSubscriber() {
    const me = this;
    me.ratingChangesSubscriber = me._ratingService.ObserveChanges.subscribe((changedRatings) => {
      // check, is one of the rating a news key, then reload the view, with new data
      let newsRatingChanged = false;
      const newsKeyPrefix = ContentType.News + "|";
      for (const changedRating of changedRatings) {
        if (changedRating.toString().startsWith(newsKeyPrefix)) {
          newsRatingChanged = true;
          break;
        }
      }
      if (newsRatingChanged) {
        me.ReloadDataAndRender();
      }
    });
  }

  /* 
    get news data from storage
  */
  private ReloadDataAndRender(): void {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    me._newsService.GetNewsViewModel(me.newsId)
      .then(event => {
        if (event) {
          me.hasData = true;
          me.newsViewModel = event;
          me.PushChanges();
        } else {
          me.hasData = false;
          // to avoid error for first binding
          //me.newsViewModel = new NewsDetailViewModel();
          me.PushChanges();
        }
        setTimeout(() => {
          me.PushChanges();
        }, 200);
        me.initialAnimation();
        me._overlaySpinnerService.HideLayoutSpinner();
      })
      .catch(e => {
        me._coreLogService.error("NewsDetailComponent.ReloadDataAndRender", "GetNewsViewModel", e);
        me._overlaySpinnerService.HideLayoutSpinner();
        me._snackBarService.ShowErrorMessageFailed(73);
      });
  }

  /**
* initial animation after page loading
*/
  private initialAnimation() {
    const me = this;
    setTimeout(() => {
      const headerTitleContainerElement: HTMLElement = document.getElementById("headerTitleContainer");
      const headerTitleElement: HTMLElement = document.getElementById("headerTitle");
      const shiftPixels = headerTitleContainerElement.clientWidth - headerTitleElement.clientWidth;
      if (shiftPixels < 0) {
        me.floatingTextActivated = true;
        me.shiftPixels = shiftPixels; // 20 = padding
        me.animationObject = {
          value: "right", params: {
            shiftPixels: `${(me.shiftPixels / 2) - 20}px` // 20 is padding in header-title class
            ,
            initialShift: `${((me.shiftPixels * (-1)) / 2) + 10}px`,
            initialtime: "3s",
            startPosition: "0px"
          }
        };
        me.PushChanges();
      }
    }, 500);
  }

  // when countDown ends it must be hidden from the view.
  public countdownEnding(): void {
    this.PushChanges();
  }

  /**
   * Open a ModalDialog for rating a news or watching news rating info
   * @param news is empty if there is no rating yet. sending item Id and contentType to show rating data in modalDialog if user rate this item before.
   */
  public openRateModel(news: NewsDetailViewModel): void {
    const me = this;
    const ratingModel = me._bottomSheetHelperService.open(RatingDialogComponent, {
      data: { contentId: news.id, contentType: ContentType.News, title: news.label },
    });
    // show snackBar after rating Item
    ratingModel.afterDismissed().subscribe((result: any) => {
      if (result) {
        result
          .then(() => {
            me._snackBarService.ShowMessage(snackBarMessageKey.successfullyExecuted, SnackBarMessageType.Successful);
          })
          .catch(e => {
            me._coreLogService.error("NewsDetailComponent.openRateModel", "afterClosed", e);
            me._snackBarService.ShowErrorMessageFailed(74);
          });
      }
    });
  }

  public navigateBack() {
    this._routerHelperService.navigateBackSimple();
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._langSubscriber) {
      me._langSubscriber.unsubscribe();
    }
    if (me._newsChangesSubscriber) {
      me._newsChangesSubscriber.unsubscribe();
    }
    if (me.ratingChangesSubscriber) {
      me.ratingChangesSubscriber.unsubscribe();
    }
  }
}
